import { useState, useEffect } from 'react';
import { History, Loader2, Search, Building } from 'lucide-react';
import api from '../api';

function Ledger() {
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchLedger = async () => {
      try {
        const res = await api.get('/ledger');
        setLedger(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchLedger();
  }, []);

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><Loader2 className="text-muted" style={{ animation: 'spin 2s linear infinite' }} /></div>;
  }

  const filteredLedger = ledger.filter(entry => 
    (entry.fund_name?.toLowerCase() || 'bank account').includes(searchTerm.toLowerCase()) ||
    (entry.income_name?.toLowerCase() || 'manual transfer').includes(searchTerm.toLowerCase())
  );

  return (
    <div className="animate-fade-in" style={{ paddingBottom: '2rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ padding: '0.5rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px', color: 'var(--primary)' }}>
            <History size={24} />
          </div>
          <h1 style={{ margin: 0 }}>Transaction History</h1>
        </div>
        
        <div style={{ position: 'relative' }}>
          <Search size={16} className="text-muted" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
          <input 
            type="text" 
            placeholder="Search funds or incomes..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ 
              padding: '0.6rem 1rem 0.6rem 35px', 
              borderRadius: '8px',
              border: '1px solid var(--surface-border)',
              background: 'rgba(255,255,255,0.02)',
              color: 'var(--text)',
              width: '300px',
              maxWidth: '100%'
            }} 
          />
        </div>
      </div>

      <div className="glass" style={{ padding: '0' }}>
        {ledger.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <History size={48} style={{ opacity: 0.2, margin: '0 auto 1rem auto' }} />
            <p>No transaction history found.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--surface-border)', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                  <th style={{ padding: '1rem', fontWeight: 600 }}>Date</th>
                  <th style={{ padding: '1rem', fontWeight: 600 }}>Type</th>
                  <th style={{ padding: '1rem', fontWeight: 600 }}>Bucket</th>
                  <th style={{ padding: '1rem', fontWeight: 600, textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {filteredLedger.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                      No matching transactions found.
                    </td>
                  </tr>
                ) : (
                  filteredLedger.map((entry) => (
                    <tr key={entry.id} style={{ borderBottom: '1px solid var(--surface-border)' }}>
                    <td style={{ padding: '1rem', color: 'var(--text-muted)' }}>
                      {new Date(entry.created_at).toLocaleString(undefined, {
                        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                      })}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {entry.is_gross_deposit ? (
                          <span style={{ fontSize: '0.85rem', padding: '0.2rem 0.6rem', backgroundColor: 'rgba(34, 197, 94, 0.1)', color: 'var(--success)', borderRadius: '12px', fontWeight: 600 }}>
                            Gross Paycheck Deposit
                          </span>
                        ) : entry.amount < 0 ? (
                          <span style={{ fontSize: '0.85rem', padding: '0.2rem 0.6rem', backgroundColor: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger)', borderRadius: '12px' }}>
                            Auto-Paid Bill
                          </span>
                        ) : !entry.fund_name ? (
                          <span style={{ fontSize: '0.85rem', padding: '0.2rem 0.6rem', backgroundColor: 'rgba(34, 197, 94, 0.1)', color: 'var(--success)', borderRadius: '12px' }}>
                            Paycheck Remainder
                          </span>
                        ) : entry.income_name ? (
                          <span style={{ fontSize: '0.85rem', padding: '0.2rem 0.6rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', color: 'var(--primary)', borderRadius: '12px' }}>
                            Bucket Funding
                          </span>
                        ) : (
                          <span style={{ fontSize: '0.85rem', padding: '0.2rem 0.6rem', backgroundColor: 'rgba(168, 162, 158, 0.1)', color: 'var(--text-muted)', borderRadius: '12px' }}>
                            Manual Transfer
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {entry.is_gross_deposit ? (
                          <Building size={16} color="var(--success)" />
                        ) : entry.fund_color ? (
                          <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: entry.fund_color }} />
                        ) : (
                          <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: 'var(--success)' }} />
                        )}
                        <span style={{ fontWeight: 500 }}>
                           {entry.is_gross_deposit ? 'Main Bank Account' : (entry.fund_name || 'Left in Checking')}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right', fontWeight: 600, color: entry.amount >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                      {entry.amount >= 0 ? '+' : '-'}${Math.abs(entry.amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Ledger;
